import {
    ButtonGhost,
    ButtonGradientPrimary,
    ButtonSecondary,
    Icon,
    ModalDialog,
    SearchBar,
    SelectFilled,
    Stepper,
} from '@amelio/ui-library';
import { useMemo, useState } from 'react';

import type { Competency, CompetencyGroup } from '@/types/competency/Competency';

import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { CompetencyStatus } from '@/types/competency/Competency';
import { getTextByLanguage } from '@/utils/localization';

type AddCompetenciesModalProps = {
    /** The tab mounts this dialog only while a profile is being extended, so it opens by default. */
    isOpen?: boolean;
    groups: CompetencyGroup[];
    // Competency ids already in the profile — shown but locked (can't be added again).
    lockedCompetencyIds: string[];
    // Currently added (overlay) competency ids, pre-checked.
    selectedCompetencyIds: string[];
    onConfirm: (competencyIds: string[]) => void;
    onCancel: () => void;
    profileName?: Record<string, string>;
};

const ALL_CATEGORIES = '-1';

// Grouped competency catalog with search, category filter and checkboxes, used by the
// profile selector to ADD extra competencies for one employee. Supports a 2-step review flow.
export const CompetencyAddModal = ({
    isOpen = true,
    groups,
    lockedCompetencyIds,
    selectedCompetencyIds,
    onConfirm,
    onCancel,
    profileName,
}: AddCompetenciesModalProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();

    const lockedSet = useMemo(() => new Set(lockedCompetencyIds), [lockedCompetencyIds]);
    const [selected, setSelected] = useState<Set<string>>(() => new Set(selectedCompetencyIds));
    const [search, setSearch] = useState('');
    const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>(ALL_CATEGORIES);
    const [isReviewStep, setIsReviewStep] = useState(false);

    const categoryOptions = useMemo(
        () => [
            {
                id: ALL_CATEGORIES,
                value: ALL_CATEGORIES,
                label: t('competencies.competencyProfilesAdmin.addModal.allCategories'),
            },
            ...groups.map((group) => ({
                id: group.id,
                value: group.id,
                label: getTextByLanguage(group.name, language),
            })),
        ],
        [groups, language, t],
    );

    const toggle = (competencyId: string) => {
        if (lockedSet.has(competencyId)) {
            return;
        }

        setSelected((current) => {
            const next = new Set(current);
            if (next.has(competencyId)) {
                next.delete(competencyId);
            } else {
                next.add(competencyId);
            }

            return next;
        });
    };

    const toggleGroup = (competencyIds: string[]) => {
        const unaddedIds = competencyIds.filter((id) => !lockedSet.has(id));
        if (unaddedIds.length === 0) {
            return;
        }

        setSelected((current) => {
            const next = new Set(current);
            const allSelected = unaddedIds.every((id) => next.has(id));

            if (allSelected) {
                unaddedIds.forEach((id) => next.delete(id));
            } else {
                unaddedIds.forEach((id) => next.add(id));
            }

            return next;
        });
    };

    const removeFromReview = (competencyId: string) => {
        setSelected((current) => {
            const next = new Set(current);
            next.delete(competencyId);
            return next;
        });
    };

    const handleCancel = () => {
        setSearch('');
        setSelectedCategoryFilter(ALL_CATEGORIES);
        setSelected(new Set(selectedCompetencyIds));
        setIsReviewStep(false);
        onCancel();
    };

    // Only Active competencies are offered; the search matches name + description and
    // the category filter narrows by competency group.
    const filteredGroups = useMemo(() => {
        const query = search.trim().toLowerCase();

        return groups
            .map((group) => ({
                group,
                competencies: group.competencies.filter((competency) => {
                    if (competency.status !== CompetencyStatus.ACTIVE) {
                        return false;
                    }

                    if (
                        selectedCategoryFilter !== ALL_CATEGORIES &&
                        group.id !== selectedCategoryFilter
                    ) {
                        return false;
                    }

                    if (!query) {
                        return true;
                    }

                    const haystack = `${getTextByLanguage(
                        competency.name,
                        language,
                    )} ${getTextByLanguage(competency.description, language)}`.toLowerCase();

                    return haystack.includes(query);
                }),
            }))
            .filter((entry) => entry.competencies.length > 0);
    }, [groups, search, language, selectedCategoryFilter]);

    const allCompetencies = useMemo(() => groups.flatMap((group) => group.competencies), [groups]);

    const findCompetency = (competencyId: string): Competency | undefined =>
        allCompetencies.find((competency) => competency.id === competencyId);

    const findGroup = (competencyId: string): CompetencyGroup | undefined =>
        groups.find((group) =>
            group.competencies.some((competency) => competency.id === competencyId),
        );

    const title = profileName
        ? t('competencies.competencyProfilesAdmin.addModal.titleWithProfile', {
              profile: getTextByLanguage(profileName, language),
          })
        : t('competencies.competencyProfilesAdmin.addModal.title');

    const totalCompetencies = lockedCompetencyIds.length + selected.size;

    const steps = [
        {
            key: 'select',
            label: t('competencies.profiles.wizard.stepSelect'),
        },
        {
            key: 'review',
            label: t('competencies.competencyProfilesAdmin.addModal.reviewTitle'),
        },
    ];

    return (
        <ModalDialog
            isOpen={isOpen}
            onClose={handleCancel}
            closeAriaLabel={t('common.close')}
            // The window keeps the name of what it does; the stepper and the subtitle say which step
            // is shown.
            title={title}
            subtitle={
                isReviewStep
                    ? t('competencies.competencyProfilesAdmin.addModal.reviewSubtitle')
                    : t('competencies.competencyProfilesAdmin.addModal.subtitle')
            }
            size={'md'}
            backdropBlur
            dataTestId={'profile-add-competency-modal'}
            headerExtra={
                <div className={'flex flex-col gap-4'}>
                    <Stepper
                        steps={steps}
                        current={isReviewStep ? 2 : 1}
                        variant={'pills'}
                        dataTestId={'roster-add-stepper'}
                    />

                    {!isReviewStep && (
                        <div className={'flex flex-col items-center gap-4 sm:flex-row'}>
                            <div className={'w-full flex-1'}>
                                <SearchBar
                                    size={'md'}
                                    value={search}
                                    onChange={(value) => setSearch(value)}
                                    placeholder={t(
                                        'competencies.competencyProfilesAdmin.addModal.searchPlaceholder',
                                    )}
                                    dataTestId={'roster-add-search'}
                                    className={
                                        'border border-gray-200 focus-within:border-2 focus-within:border-teal-400'
                                    }
                                />
                            </div>

                            <div className={'w-full sm:w-56'}>
                                <SelectFilled
                                    value={selectedCategoryFilter}
                                    options={categoryOptions}
                                    onChange={(value) => setSelectedCategoryFilter(value)}
                                    size={'md'}
                                    ariaLabel={t(
                                        'competencies.competencyProfilesAdmin.addModal.allCategories',
                                    )}
                                    dataTestId={'roster-add-category-filter'}
                                    className={
                                        'text-md text-navy border border-gray-200 bg-white font-normal'
                                    }
                                />
                            </div>
                        </div>
                    )}
                </div>
            }
            footer={
                <div className={'flex items-center justify-between gap-3'}>
                    {isReviewStep ? (
                        <div className={'ml-auto flex items-center gap-3'}>
                            <ButtonSecondary
                                size={'md'}
                                onClick={() => setIsReviewStep(false)}
                                leftIcon={
                                    <Icon
                                        name={'arrow-arrow-left'}
                                        size={'sm'}
                                        colorClassName={'text-base-500'}
                                        ariaHidden
                                    />
                                }
                                dataTestId={'roster-add-back'}
                                className={'border border-gray-200'}
                                fitContent
                            >
                                {t('competencies.competencyProfilesAdmin.addModal.back')}
                            </ButtonSecondary>

                            <ButtonGradientPrimary
                                size={'md'}
                                disabled={selected.size === 0}
                                onClick={() => onConfirm(Array.from(selected))}
                                dataTestId={'roster-add-confirm'}
                                fitContent
                            >
                                {t('competencies.competencyProfilesAdmin.addModal.confirm', {
                                    count: selected.size,
                                })}
                            </ButtonGradientPrimary>
                        </div>
                    ) : (
                        <>
                            <span className={'text-sm font-medium text-gray-500'}>
                                {t('competencies.competencyProfilesAdmin.addModal.selectedCount', {
                                    count: selected.size,
                                })}
                            </span>

                            <div className={'flex items-center gap-3'}>
                                <ButtonSecondary
                                    size={'md'}
                                    onClick={handleCancel}
                                    dataTestId={'roster-add-cancel'}
                                    className={'border border-gray-200'}
                                    fitContent
                                >
                                    {t('competencies.competencyProfilesAdmin.addModal.cancel')}
                                </ButtonSecondary>

                                <ButtonGradientPrimary
                                    size={'md'}
                                    disabled={selected.size === 0}
                                    onClick={() => setIsReviewStep(true)}
                                    dataTestId={'roster-add-review'}
                                    fitContent
                                >
                                    {t('competencies.competencyProfilesAdmin.addModal.review', {
                                        count: selected.size,
                                    })}
                                </ButtonGradientPrimary>
                            </div>
                        </>
                    )}
                </div>
            }
        >
            {isReviewStep ? (
                <div className={'flex flex-col gap-4'}>
                    <p className={'text-md text-gray-600'}>
                        {t('competencies.competencyProfilesAdmin.addModal.reviewSummary', {
                            current: lockedCompetencyIds.length,
                            new: selected.size,
                            total: totalCompetencies,
                        })}
                    </p>

                    <ul className={'flex flex-col gap-3'}>
                        {Array.from(selected).map((id) => {
                            const competency = findCompetency(id);
                            const group = findGroup(id);

                            if (!competency) {
                                return null;
                            }

                            return (
                                <li
                                    key={id}
                                    className={
                                        'border-primary/30 bg-primary/5 flex items-center justify-between gap-3 rounded-lg border p-3'
                                    }
                                >
                                    <div className={'min-w-0 flex-1'}>
                                        <p className={'text-md text-navy font-medium'}>
                                            {getTextByLanguage(competency.name, language)}
                                        </p>

                                        <p className={'text-xs text-gray-600'}>
                                            {getTextByLanguage(group?.name ?? {}, language)}
                                        </p>
                                    </div>

                                    <ButtonGhost
                                        size={'sm'}
                                        onClick={() => removeFromReview(id)}
                                        ariaLabel={t(
                                            'competencies.competencyProfilesAdmin.selector.remove',
                                        )}
                                        leftIcon={
                                            <Icon
                                                name={'essential-close'}
                                                size={'sm'}
                                                colorClassName={'text-base-500'}
                                                ariaHidden
                                            />
                                        }
                                        dataTestId={`roster-add-review-remove-${id}`}
                                    />
                                </li>
                            );
                        })}
                    </ul>

                    {profileName && (
                        <div
                            className={
                                'text-md text-navy bg-warning/10 mt-2 rounded-lg border border-orange-200 p-4'
                            }
                        >
                            {t('competencies.competencyProfilesAdmin.addModal.impactWarning', {
                                profile: getTextByLanguage(profileName, language),
                            })}
                        </div>
                    )}
                </div>
            ) : filteredGroups.length === 0 ? (
                <p className={'py-12 text-center text-base text-gray-600'}>
                    {t('competencies.competencyProfilesAdmin.addModal.empty')}
                </p>
            ) : (
                <div className={'flex flex-col gap-6'}>
                    {filteredGroups.map(({ group, competencies }) => {
                        const unaddedInGroup = competencies.filter(
                            (competency) => !lockedSet.has(competency.id),
                        );
                        const allGroupSelected =
                            unaddedInGroup.length > 0 &&
                            unaddedInGroup.every((competency) => selected.has(competency.id));
                        const allAlreadyAdded =
                            competencies.length > 0 &&
                            competencies.every((competency) => lockedSet.has(competency.id));

                        return (
                            <div key={group.id}>
                                <div
                                    className={
                                        'mb-2 flex flex-col items-center gap-2 sm:flex-row sm:justify-between'
                                    }
                                >
                                    <p className={'text-navy text-lg font-semibold'}>
                                        {getTextByLanguage(group.name, language)}
                                    </p>

                                    {allAlreadyAdded ? (
                                        <span
                                            className={'text-xs font-medium text-teal-500'}
                                            data-testid={`roster-add-group-all-added-${group.id}`}
                                        >
                                            <Icon
                                                name={'essential-checkmark'}
                                                size={'sm'}
                                                className={'mr-1 inline-block'}
                                                colorClassName={'text-primary'}
                                                ariaHidden
                                            />
                                            {t(
                                                'competencies.competencyProfilesAdmin.addModal.allAdded',
                                            )}
                                        </span>
                                    ) : (
                                        <ButtonSecondary
                                            size={'sm'}
                                            fitContent
                                            onClick={() =>
                                                toggleGroup(
                                                    unaddedInGroup.map(
                                                        (competency) => competency.id,
                                                    ),
                                                )
                                            }
                                            leftIcon={
                                                <Icon
                                                    name={
                                                        allGroupSelected
                                                            ? 'essential-checkmark'
                                                            : 'essential-add'
                                                    }
                                                    size={'sm'}
                                                    colorClassName={'text-teal-500'}
                                                    ariaHidden
                                                />
                                            }
                                            className={
                                                'border border-teal-400/30 font-normal text-teal-500'
                                            }
                                            dataTestId={`roster-add-group-toggle-${group.id}`}
                                        >
                                            {allGroupSelected
                                                ? t(
                                                      'competencies.competencyProfilesAdmin.addModal.groupSelected',
                                                      { count: unaddedInGroup.length },
                                                  )
                                                : t(
                                                      'competencies.competencyProfilesAdmin.addModal.selectGroup',
                                                      { count: unaddedInGroup.length },
                                                  )}
                                        </ButtonSecondary>
                                    )}
                                </div>

                                <div className={'flex flex-col gap-2'}>
                                    {competencies.map((competency) => {
                                        const isLocked = lockedSet.has(competency.id);
                                        const isPending = selected.has(competency.id);

                                        const onRowActivate = () => {
                                            if (!isLocked) {
                                                toggle(competency.id);
                                            }
                                        };

                                        return (
                                            <div
                                                key={competency.id}
                                                role={'button'}
                                                tabIndex={isLocked ? -1 : 0}
                                                aria-disabled={isLocked}
                                                aria-pressed={isLocked ? undefined : isPending}
                                                onClick={onRowActivate}
                                                onKeyDown={(event) => {
                                                    if (
                                                        !isLocked &&
                                                        (event.key === 'Enter' || event.key === ' ')
                                                    ) {
                                                        event.preventDefault();
                                                        onRowActivate();
                                                    }
                                                }}
                                                className={
                                                    'flex items-start gap-3 rounded-xl border p-4 ' +
                                                    (isLocked
                                                        ? 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-60'
                                                        : isPending
                                                          ? 'text-navy border-primary bg-primary/5 ring-primary/30 cursor-pointer ring-1'
                                                          : 'text-navy hover:border-primary hover:bg-base-25 cursor-pointer border-gray-200 bg-white')
                                                }
                                                data-testid={`roster-add-competency-${competency.id}`}
                                            >
                                                <div className={'min-w-0 flex-1'}>
                                                    <div className={'flex items-center gap-2'}>
                                                        <p className={'text-navy font-medium'}>
                                                            {getTextByLanguage(
                                                                competency.name,
                                                                language,
                                                            )}
                                                        </p>

                                                        {isLocked && (
                                                            <span
                                                                className={
                                                                    'bg-base-25 shrink-0 rounded-full px-2 py-0.5 text-xs font-medium text-teal-400'
                                                                }
                                                            >
                                                                {t(
                                                                    'competencies.competencyProfilesAdmin.addModal.inProfile',
                                                                )}
                                                            </span>
                                                        )}

                                                        {isPending && (
                                                            <span
                                                                className={
                                                                    'bg-primary/10 shrink-0 rounded-full px-2 py-0.5 text-xs font-medium text-teal-400'
                                                                }
                                                            >
                                                                <Icon
                                                                    name={'essential-checkmark'}
                                                                    size={'sm'}
                                                                    className={'mr-1 inline-block'}
                                                                    colorClassName={'text-primary'}
                                                                    ariaHidden
                                                                />
                                                                {t(
                                                                    'competencies.competencyProfilesAdmin.addModal.selected',
                                                                )}
                                                            </span>
                                                        )}
                                                    </div>

                                                    <p className={'text-xs text-gray-600'}>
                                                        {getTextByLanguage(
                                                            competency.description,
                                                            language,
                                                        )}
                                                    </p>
                                                </div>

                                                {!isLocked && !isPending && (
                                                    <Icon
                                                        name={'essential-add'}
                                                        size={'md'}
                                                        colorClassName={'text-base-500'}
                                                        className={'flex-shrink-0'}
                                                        ariaHidden
                                                    />
                                                )}

                                                {(isLocked || isPending) && (
                                                    <>
                                                        <Icon
                                                            name={'essential-checkmark'}
                                                            size={'sm'}
                                                            colorClassName={'text-primary'}
                                                            className={'flex-shrink-0'}
                                                            ariaHidden
                                                        />
                                                        <span
                                                            className={
                                                                'text-xs font-normal text-teal-500'
                                                            }
                                                        >
                                                            {t(
                                                                'competencies.competencyProfilesAdmin.addModal.added',
                                                            )}
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </ModalDialog>
    );
};
